// src/pages/RequestPasswordResetPage.tsx
import React, { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import { requestPasswordReset, clearAuthStatus } from '../features/auth/authSlice';
import { useTranslation } from 'react-i18next'; // ADDED: Import useTranslation
import { Typography, Box, TextField, Button, CircularProgress, Alert, Link } from '@mui/material';
import AuthLayout from '../layout/AuthLayout';
import { Link as RouterLink } from 'react-router-dom';

const RequestPasswordResetPage: React.FC = () => {
    const { t } = useTranslation(); // ADDED: Initialize translation
    const dispatch = useAppDispatch();
    const { status, error, message } = useAppSelector((state) => state.auth);
    const [email, setEmail] = useState('');

    useEffect(() => {
        dispatch(clearAuthStatus());
    }, [dispatch]);

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (email) {
            dispatch(requestPasswordReset({ email }));
        }
    };

    const isLoading = status.auth === 'loading';
    const isSucceeded = status.auth === 'succeeded' && !!message;

    return (
        <AuthLayout>
            {isSucceeded ? (
                <>
                    <Typography component="h1" variant="h5">{t('request_password_reset_success')}</Typography>
                    <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1, mb: 2 }}>
                        {t('request_password_reset_success_instructions')}
                    </Typography>
                    {message && <Alert severity="success" sx={{ width: '100%', mt: 2 }}>{message}</Alert>}
                    <Box textAlign="center" sx={{ mt: 2 }}>
                        <Link component={RouterLink} to="/login" color="inherit">
                            {t('back_to_sign_in')}
                        </Link>
                    </Box>
                </>
            ) : (
                <>
                    <Typography component="h1" variant="h5">{t('reset_password')}</Typography>
                    <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1, mb: 2 }}>
                        {t('reset_password_instructions')}
                    </Typography>

                    {error && <Alert severity="error" sx={{ width: '100%', mt: 2 }}>{error}</Alert>}

                    <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1, width: '100%' }}>
                        <TextField
                            margin="normal" required fullWidth id="email" label={t('email_address')} name="email"
                            value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" autoFocus
                            disabled={isLoading}
                        />
                        <Button
                            type="submit" fullWidth variant="contained"
                            sx={{
                                mt: 3, mb: 2,
                                bgcolor: 'inverse.main', color: 'inverse.contrastText', '&:hover': { bgcolor: 'inverse.hover.main' }
                            }}
                            disabled={isLoading}
                        >
                            {isLoading ? <CircularProgress size={24} color="inherit" /> : t('send_reset_link')}
                        </Button>
                    </Box>

                    <Box textAlign="center" sx={{ mt: 1 }}>
                        <Link component={RouterLink} to="/login" color="inherit">
                            {t('back_to_sign_in')}
                        </Link>
                    </Box>
                </>
            )}
        </AuthLayout>
    );
};

export default RequestPasswordResetPage;